import styled from "@emotion/styled";
import { IconButton, SxProps, Theme } from "@mui/material";
import BaseTooltip from "@ui/atoms/BaseTooltip/BaseTooltip";
import Icon from "@ui/atoms/Icon/Icon";
import { IconName } from "@ui/atoms/Icon/registry";
import { MouseEvent, ReactNode } from "react";

export interface BaseIconButtonProps {
  icon: IconName;
  /** Accessible name, and the tooltip unless `tooltip` says otherwise. */
  label: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Tooltip content; `false` for none. Defaults to `label`. */
  tooltip?: ReactNode | false;
  tooltipPlacement?: "top" | "right" | "bottom" | "left";
  /**
   * Filled in the brand colour, e.g. a starred favourite. Passing it marks the
   * button as a toggle (`aria-pressed`); leave it out for a plain action.
   */
  selected?: boolean;
  disabled?: boolean;
  dataTestId?: string;
  className?: string;
  sx?: SxProps<Theme>;
}

const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "selected"
})<{ selected: boolean }>(({ selected, theme }) => {
  const { palette } = theme.vars;
  return {
    "&:hover": {
      backgroundColor: selected ? palette.primary.dark : palette.grey[300],
      borderColor: selected ? palette.primary.dark : palette.border.subtle
    },
    backgroundColor: selected ? palette.primary.light : "transparent",
    border: `1px solid ${selected ? palette.primary.light : palette.border.hairline}`,
    borderRadius: theme.shape.borderRadiusButton,
    boxSizing: "border-box",
    color: selected ? palette.primary.contrastText : palette.text.secondary,
    flexShrink: 0,
    height: theme.controls.height,
    padding: 0,
    transition: "background-color 120ms ease, border-color 120ms ease",
    width: theme.controls.height
  };
});

/**
 * An outlined, control-height button holding one glyph: a row action, a
 * favourite star, a pager arrow. The name lives in the tooltip and label.
 */
export const BaseIconButton = ({
  className,
  dataTestId,
  disabled,
  icon,
  label,
  onClick,
  selected,
  sx,
  tooltip,
  tooltipPlacement = "top"
}: BaseIconButtonProps) => {
  const button = (
    <StyledIconButton
      aria-label={label}
      aria-pressed={selected}
      className={className}
      data-test-id={dataTestId}
      disabled={disabled}
      onClick={onClick}
      selected={Boolean(selected)}
      sx={sx}
    >
      <Icon name={icon} />
    </StyledIconButton>
  );

  if (tooltip === false) return button;

  return (
    <BaseTooltip title={tooltip ?? label} placement={tooltipPlacement}>
      {/* A disabled button fires no events, so the tooltip needs a host. */}
      {disabled ? <span>{button}</span> : button}
    </BaseTooltip>
  );
};

export default BaseIconButton;
